<template>
  <v-dialog v-model="dialog" max-width="760" persistent>
    <v-card class="vehicle-wizard">
      <v-card-title class="d-flex align-center">
        {{ $t('vehicle.wizard.title') }}
        <v-spacer />
        <div class="vw-steps">
          <div
              v-for="(s, i) in visibleSteps"
              :key="s.key"
              class="vw-step"
              :class="{ 'vw-step--active': stepIndex === i, 'vw-step--done': stepIndex > i }"
          >
            <span class="vw-step__num"><v-icon v-if="stepIndex > i" x-small color="white">mdi-check</v-icon><template v-else>{{ i + 1 }}</template></span>
            <span class="vw-step__label">{{ s.label }}</span>
          </div>
        </div>
      </v-card-title>

      <v-card-text class="pt-4">

        <!-- STEP vehicle: lookup / create / assign-existing -->
        <section v-show="currentKey === 'vehicle'" class="vw-body">
          <template v-if="vehicleStep === 'lookup'">
            <p class="mb-4">{{ $t('vehicle.lookup-registration') }}</p>
            <v-text-field
                :label="$t('vehicle.registration-number')"
                v-model="registrationNumber"
                outlined
                hide-details="auto"
                @input="normalizeRegistration"
                @keyup.enter="doLookup"
                :error-messages="lookupError"
            />
          </template>

          <template v-else-if="vehicleStep === 'create'">
            <v-alert type="info" text dense class="mb-4">
              {{ $t('vehicle.no-vehicle-found') }} — <strong>{{ registrationNumber }}</strong>
            </v-alert>
            <v-form ref="createForm">
              <v-text-field :label="$t('vehicle.registration-number')" :value="registrationNumber" outlined hide-details disabled class="mb-4" />
              <div class="subtitle-1 font-weight-bold mb-2">
                {{ $t('vehicle.brand') }} / {{ $t('vehicle.model') }} / {{ $t('vehicle.version') }}
              </div>
              <vehicle-brand-model-version-selector v-model="createForm.versionId" :rules="[v => !!v || $t('required')]" />
            </v-form>
          </template>

          <template v-else-if="vehicleStep === 'assign-existing'">
            <v-alert type="success" text dense class="mb-4">{{ $t('vehicle.vehicle-found') }}</v-alert>
            <v-simple-table dense class="mb-4">
              <tbody>
                <tr><td class="font-weight-bold">{{ $t('vehicle.registration-number') }}</td><td>{{ foundVehicle.registrationNumber }}</td></tr>
                <tr v-if="foundVehicle.name"><td class="font-weight-bold">{{ $t('vehicle.name') }}</td><td>{{ foundVehicle.name }}</td></tr>
                <tr v-if="foundVehicle.brand"><td class="font-weight-bold">{{ $t('vehicle.brand') }} / {{ $t('vehicle.model') }}</td><td>{{ foundVehicle.brand }} {{ foundVehicle.model || '' }}</td></tr>
                <tr v-if="foundVehicle.organization"><td class="font-weight-bold">{{ $t('vehicle.organization') }}</td><td>{{ foundVehicle.organization.name }}</td></tr>
                <tr v-if="foundVehicle.customer"><td class="font-weight-bold">{{ $t('vehicle.customer') }}</td><td>{{ foundVehicle.customer | customerLabel }}</td></tr>
              </tbody>
            </v-simple-table>
          </template>
        </section>

        <!-- STEP owner: who the vehicle belongs to — customer (fixed), member (fixed/auto), or
             organization (fixed or chosen) with an OPTIONAL member of that org. -->
        <section v-show="currentKey === 'owner'" class="vw-body">

          <!-- Fixed customer (launched from a customer): the driver, no org owner -->
          <div v-if="ownerContext === 'customer'" class="d-flex align-center">
            <v-icon left small color="primary">mdi-account</v-icon>
            <span class="ml-1">{{ $t('vehicle.wizard.driver') }} : <strong>{{ customerName }}</strong></span>
          </div>

          <!-- Fixed member (launched from a member): org owns it, person drives it -->
          <template v-else-if="ownerContext === 'member'">
            <div class="d-flex align-center mb-1">
              <v-icon left small color="primary">mdi-office-building</v-icon>
              <span class="ml-1">{{ $t('vehicle.wizard.owner') }} : <strong>{{ organizationName }}</strong></span>
            </div>
            <div class="d-flex align-center">
              <v-icon left small color="primary">mdi-account</v-icon>
              <span class="ml-1">{{ $t('vehicle.wizard.driver') }} : <strong>{{ customerName }}</strong></span>
            </div>
          </template>

          <!-- Organization owner (fixed org, or global with the "organization" choice) -->
          <template v-else>
            <!-- Global: pick whether the owner is a standalone customer or an organization -->
            <template v-if="ownerContext === 'global'">
              <p class="mb-2">{{ $t('vehicle.wizard.owner-intro') }}</p>
              <v-radio-group v-model="globalOwnerType" row class="mt-0 mb-2">
                <v-radio value="customer" :label="$t('vehicle.wizard.owner-a-customer')" />
                <v-radio value="organization" :label="$t('vehicle.wizard.owner-an-organization')" />
              </v-radio-group>

              <customer-select
                  v-if="globalOwnerType === 'customer'"
                  v-model="ownerCustomerId"
                  :label="$t('vehicle.wizard.choose-customer')"
                  outlined dense clearable selection-one-line
              />
              <tree-select-organization
                  v-else
                  v-model="ownerOrganizationId"
                  :label="$t('organization.organization')"
              />
            </template>

            <!-- Fixed org (launched from an organization) -->
            <div v-else class="d-flex align-center mb-2">
              <v-icon left small color="primary">mdi-office-building</v-icon>
              <span class="ml-1">{{ $t('vehicle.wizard.owner') }} : <strong>{{ organizationName }}</strong></span>
            </div>

            <!-- Driver (member) of the (fixed or chosen) organization: pick an EXISTING member. The member
                 must already exist (they carry the customer the vehicle is linked to and whose record the
                 consent later updates); creation is out of scope. -->
            <div v-if="effectiveOrganizationId" class="mt-4">
              <div class="text-subtitle-2 mb-1">{{ $t('vehicle.wizard.driver-optional') }}</div>
              <member-select
                  v-model="ownerMemberId"
                  :organization-id="effectiveOrganizationId"
                  :label="$t('vehicle.wizard.choose-driver')"
                  clearable
              />
            </div>
          </template>
        </section>

        <!-- STEP connect: does this vehicle get a telematics connection? -->
        <section v-show="currentKey === 'connect'" class="vw-body text-center py-4">
          <v-icon size="40" color="grey lighten-1" class="mb-3">mdi-car-connected</v-icon>
          <div class="text-h6 mb-6">{{ $t('vehicle.wizard.connect-question') }}</div>
          <div class="d-flex justify-center flex-wrap" style="gap: 12px">
            <v-btn outlined @click="finishWithoutConnection" :loading="saving">
              {{ $t('vehicle.wizard.connect-no') }}
            </v-btn>
            <v-btn color="primary" :disabled="!connectionAvailable" @click="goConnectionStep">
              <v-icon left small>mdi-link-variant</v-icon>
              {{ $t('vehicle.wizard.connect-yes') }}
            </v-btn>
          </div>
          <p v-if="!connectionAvailable" class="text--secondary text-caption mt-4">
            {{ $t('vehicle.wizard.no-connection-offer') }}
          </p>
        </section>

        <!-- STEP usage: how the connection is used → the mode (telemetry vs meter) -->
        <section v-show="currentKey === 'usage'" class="vw-body">
          <p class="mb-4">{{ $t('vehicle.wizard.usage-intro') }}</p>
          <v-radio-group v-model="connectionMode" class="mt-0">
            <v-radio value="telemetry" :disabled="!modeAvailable('telemetry')">
              <template #label>
                <div>
                  <div>{{ $t('vehicle.conn-wizard.mode-telemetry') }}</div>
                  <div class="text-caption text--secondary">{{ $t('vehicle.conn-wizard.mode-telemetry-hint') }}</div>
                </div>
              </template>
            </v-radio>
            <v-radio value="meter" :disabled="!modeAvailable('meter')">
              <template #label>
                <div>
                  <div>{{ $t('vehicle.conn-wizard.mode-meter') }}</div>
                  <div class="text-caption text--secondary">{{ $t('vehicle.conn-wizard.mode-meter-hint') }}</div>
                </div>
              </template>
            </v-radio>
          </v-radio-group>
        </section>

        <!-- STEP type: connection type (vin/oauth) filtered by the usage → resolves the formula. The VIN is
             asked here when the connection needs it, and checked for eligibility at the provider. -->
        <section v-show="currentKey === 'type'" class="vw-body">
          <div class="text-subtitle-2 mb-2">{{ $t('vehicle.wizard.connection-type') }}</div>
          <v-radio-group v-model="connectionType" class="mt-0">
            <v-radio
                v-for="opt in typeOptions"
                :key="opt.value"
                :value="opt.value"
                :disabled="!opt.available"
            >
              <template #label>
                <div>
                  <div>{{ opt.label }}</div>
                  <div class="text-caption text--secondary">{{ opt.hint }}</div>
                </div>
              </template>
            </v-radio>
          </v-radio-group>

          <!-- VIN (VIN-type connections). A plain format check — 17 chars, no I/O/Q — is enough here; the
               vehicle is only declared to the provider at consent, so we don't call any web service now. -->
          <div v-if="connectionType === 'vin'" class="mt-2">
            <v-text-field
                :label="$t('vehicle.vin')" v-model="vin" outlined dense
                @input="vin = (vin || '').toUpperCase()"
                counter="17" maxlength="17" hide-details="auto"
                :rules="[vinRule]"
            />
          </div>

          <v-alert v-if="resolvedFormula" type="success" text dense class="mt-4">
            {{ $t('vehicle.wizard.formula-resolved', { name: resolvedFormula.name }) }}
            <strong v-if="resolvedFormula.price">· {{ formatPrice(resolvedFormula.price) }}</strong>
          </v-alert>
        </section>

        <!-- STEP consent: review + send the consent page -->
        <section v-show="currentKey === 'consent'" class="vw-body text-center py-4">
          <v-icon size="64" color="primary" class="mb-4">mdi-shield-check-outline</v-icon>
          <div class="text-h6 mb-2">{{ $t('vehicle.wizard.consent-title') }}</div>
          <p class="text--secondary mb-2">{{ $t('vehicle.wizard.consent-intro') }}</p>
          <v-alert type="info" text dense class="text-left mt-4">
            {{ $t('vehicle.wizard.consent-scope-meter') }}
          </v-alert>
        </section>

      </v-card-text>

      <v-card-actions>
        <v-btn v-if="canGoBack" small text @click="back">
          <v-icon left small>mdi-arrow-left</v-icon>{{ $t('back') }}
        </v-btn>
        <v-spacer />
        <v-btn small @click="close">{{ $t('cancel') }}</v-btn>

        <!-- vehicle step: only collects, never persists (create/assign happens after the owner is known) -->
        <v-btn v-if="currentKey === 'vehicle' && vehicleStep === 'lookup'" small color="primary" @click="doLookup" :disabled="!registrationNumber">
          {{ $t('search') }}
        </v-btn>
        <v-btn v-else-if="currentKey === 'vehicle'" small color="primary" @click="confirmVehicleStep">
          {{ $t('next') }}
        </v-btn>

        <!-- owner step: persist the vehicle with its owner, then advance (or finish if no connection applies) -->
        <v-btn v-else-if="currentKey === 'owner'" small color="primary" @click="confirmOwnerStep" :disabled="!ownerResolved" :loading="saving">
          {{ willHaveOrganization ? $t('next') : $t('vehicle.wizard.create') }}
        </v-btn>

        <!-- usage step (mode) -->
        <v-btn v-else-if="currentKey === 'usage'" small color="primary" @click="next" :disabled="!connectionMode">
          {{ $t('next') }}
        </v-btn>

        <!-- type step (resolves the formula; VIN required for a VIN connection) -->
        <v-btn v-else-if="currentKey === 'type'" small color="primary" @click="next" :disabled="!typeStepReady">
          {{ $t('next') }}
        </v-btn>

        <!-- consent step (final for connected branch) -->
        <v-btn v-else-if="currentKey === 'consent'" small color="primary" @click="finishWithConnection" :loading="saving">
          {{ $t('vehicle.wizard.finish') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import VehicleRepository from "@repository/VehicleRepository"
import OrganizationRepository from "@repository/OrganizationRepository"
import VehicleBrandModelVersionSelector from "@blocks/Select/VehicleBrandModelVersionSelector"
import CustomerSelect from "@blocks/Select/CustomerSelect.vue"
import MemberSelect from "@blocks/Select/MemberSelect.vue"
import TreeSelectOrganization from "@blocks/Select/TreeSelectOrganization.vue"
import RulesMixin from "@mixins/RulesMixin"

export default {
  name: 'VehicleWizardDialog',
  components: { VehicleBrandModelVersionSelector, CustomerSelect, MemberSelect, TreeSelectOrganization },
  mixins: [RulesMixin],

  props: {
    value: Boolean,
    organizationId: String,
    organizationName: String,
    customerId: String,
    customerName: String,
    memberId: String, // launched from a member: assign to (org + member customer)
  },

  data() {
    return {
      stepIndex: 0,
      wantsConnection: false,
      // vehicle sub-flow
      vehicleStep: 'lookup',
      registrationNumber: '',
      lookupError: null,
      foundVehicle: null,
      accessibleOrgIds: [],
      createForm: { versionId: null },
      // resolved after the vehicle exists
      vehicleId: null,
      // owner
      globalOwnerType: 'customer', // global flow: 'customer' | 'organization'
      ownerCustomerId: null,
      ownerOrganizationId: null,
      ownerMemberId: null, // the selected existing driver (member)
      // connection
      formulas: [],
      connectionMode: null, // 'telemetry' | 'meter' (the usage)
      connectionType: null, // 'vin' | 'oauth'
      vin: '',
      saving: false,
    }
  },

  computed: {
    dialog: {
      get() { return this.value },
      set(val) { this.$emit('input', val) },
    },

    // How the launch context fixes (or not) the owner.
    // 'member'  = launched from a member  → auto (org + person), fixed
    // 'customer'= launched from a customer → that customer, fixed
    // 'organization' = launched from an org → org fixed, optional member
    // 'global'  = launched from the global list → operator chooses
    ownerContext() {
      if (this.memberId) return 'member'
      if (this.customerId) return 'customer'
      if (this.organizationId) return 'organization'
      return 'global'
    },

    // The org in play (fixed by context, or chosen in the global flow) — enables the optional member picker.
    effectiveOrganizationId() {
      if (this.ownerContext === 'organization') return this.organizationId
      if (this.ownerContext === 'global' && this.globalOwnerType === 'organization') return this.ownerOrganizationId
      return null
    },

    ownerResolved() {
      switch (this.ownerContext) {
        case 'customer':
        case 'member':
          return true
        case 'organization':
          return true // org fixed; member optional
        case 'global':
          return this.globalOwnerType === 'customer' ? !!this.ownerCustomerId : !!this.ownerOrganizationId
        default:
          return false
      }
    },

    // A telematics connection only makes sense when the vehicle belongs to an organization (licences are
    // org-borne). A customer-owned vehicle can never be connected → we don't even ask.
    willHaveOrganization() {
      if (this.ownerContext === 'member' || this.ownerContext === 'organization') return true
      if (this.ownerContext === 'global') return this.globalOwnerType === 'organization'
      return false
    },

    // The "connect?" step only appears once we know the org actually offers a connectable formula
    // (resolved after the owner step). No offer → the wizard finishes at the owner step.
    hasConnectionStep() {
      return this.connectionAvailable
    },

    visibleSteps() {
      const steps = [
        { key: 'vehicle', label: this.$t('vehicle.wizard.step-vehicle') },
        { key: 'owner', label: this.$t('vehicle.wizard.step-owner') },
      ]
      if (this.hasConnectionStep) {
        steps.push({ key: 'connect', label: this.$t('vehicle.wizard.step-connect') })
        if (this.wantsConnection) {
          steps.push({ key: 'usage', label: this.$t('vehicle.wizard.step-usage') })
          steps.push({ key: 'type', label: this.$t('vehicle.wizard.step-connection') })
          // No location step: the home address is collected on the consent page.
          steps.push({ key: 'consent', label: this.$t('vehicle.wizard.step-consent') })
        }
      }
      return steps
    },

    currentKey() {
      return this.visibleSteps[this.stepIndex]?.key
    },

    canGoBack() {
      if (this.currentKey === 'vehicle') return this.vehicleStep !== 'lookup'
      return this.stepIndex > 0
    },

    // At least one connectable formula (any mode/type) exists for the org.
    connectionAvailable() {
      return this.formulas.length > 0
    },

    // Types available FOR THE CHOSEN USAGE (mode): a formula must match both mode and type. This is what
    // fixes the old "always meter" bug — the type no longer resolves a formula regardless of the mode.
    typeOptions() {
      const has = t => this.formulas.some(f => f.mode === this.connectionMode && f.type === t)
      return [
        { value: 'vin', label: this.$t('vehicle.wizard.type-vin'), hint: this.$t('vehicle.wizard.type-vin-hint'), available: has('vin') },
        { value: 'oauth', label: this.$t('vehicle.wizard.type-oauth'), hint: this.$t('vehicle.wizard.type-oauth-hint'), available: has('oauth') },
      ]
    },

    resolvedFormula() {
      if (!this.connectionMode || !this.connectionType) return null
      return this.formulas.find(f => f.mode === this.connectionMode && f.type === this.connectionType) || null
    },

    // A VIN is 17 chars, uppercase alphanumeric, excluding I/O/Q.
    vinValid() {
      return /^[A-HJ-NPR-Z0-9]{17}$/.test(this.vin || '')
    },

    // The type step can advance once a formula is resolved and, for a VIN connection, the VIN is valid.
    typeStepReady() {
      if (!this.resolvedFormula) return false
      return this.connectionType !== 'vin' || this.vinValid
    },
  },

  watch: {
    value(val) {
      if (val) this.reset()
    },
    effectiveOrganizationId() {
      // The chosen member must belong to the current org.
      this.ownerMemberId = null
    },
    globalOwnerType() {
      this.ownerMemberId = null
    },
    connectionMode() {
      // Keep the current type if it stays available in the new mode, else default to the first available.
      if (!this.typeOptions.find(o => o.value === this.connectionType && o.available)) {
        this.connectionType = this.typeOptions.find(o => o.available)?.value || null
      }
    },
  },

  methods: {
    reset() {
      this.stepIndex = 0
      this.wantsConnection = false
      this.vehicleStep = 'lookup'
      this.registrationNumber = ''
      this.lookupError = null
      this.foundVehicle = null
      this.createForm = { versionId: null }
      this.vehicleId = null
      this.globalOwnerType = 'customer'
      this.ownerCustomerId = null
      this.ownerOrganizationId = null
      this.ownerMemberId = null
      this.formulas = []
      this.connectionMode = null
      this.connectionType = null
      this.vin = ''
      this.saving = false
      this.loadAccessibleOrgs()
    },

    close() {
      this.dialog = false
    },

    normalizeRegistration() {
      this.registrationNumber = this.registrationNumber
          ? this.registrationNumber.toUpperCase().replace(/[\s-]/g, '')
          : ''
      this.lookupError = null
    },

    loadAccessibleOrgs() {
      OrganizationRepository.tree().then(tree => {
        this.accessibleOrgIds = this.flattenOrgIds(tree)
      }).catch(() => { this.accessibleOrgIds = [] })
    },

    flattenOrgIds(nodes) {
      let ids = []
      for (const node of nodes || []) {
        ids.push(node.id)
        if (node.children && node.children.length) ids = ids.concat(this.flattenOrgIds(node.children))
      }
      return ids
    },

    // --- vehicle sub-flow: plate lookup → create or assign existing ---
    doLookup() {
      if (!this.registrationNumber) return
      this.lookupError = null
      this.showLoading(true)
      VehicleRepository.lookup(this.registrationNumber).then(vehicle => {
        this.showLoading(false)
        this.foundVehicle = vehicle
        if (vehicle.organization && !this.accessibleOrgIds.includes(vehicle.organization.id) && !this.currentUser?.isZE) {
          this.lookupError = this.$t('vehicle.no-permission-for-vehicle')
          return
        }
        this.vehicleStep = 'assign-existing'
      }).catch(err => {
        this.showLoading(false)
        if (err.response && err.response.status === 404) {
          this.vehicleStep = 'create'
        } else {
          this.lookupError = err.response?.data?.message || err.message
        }
      })
    },

    // Vehicle step only validates + advances. Nothing is persisted until the owner is known (fixes the
    // "customerId or organizationId required" error when creating from the global list).
    confirmVehicleStep() {
      // Brand/model/version is required for a new vehicle (the selector isn't wired into v-form validation).
      if (this.vehicleStep === 'create' && !this.createForm.versionId) {
        this.$dialog.notify.warning(this.$t('vehicle.wizard.version-required'))
        return
      }
      this.next()
    },

    // The owner the operator resolved, as { organizationId?, customerId?, memberId? }.
    resolvedOwner() {
      switch (this.ownerContext) {
        case 'customer':
          return { customerId: this.customerId }
        case 'member':
          // Member = (org + the member's customer); creating with both makes it the member's vehicle.
          return { organizationId: this.organizationId, customerId: this.customerId, memberId: this.memberId }
        case 'organization':
          return { organizationId: this.organizationId, memberId: this.ownerMemberId || null }
        case 'global':
          return this.globalOwnerType === 'customer'
              ? { customerId: this.ownerCustomerId }
              : { organizationId: this.ownerOrganizationId, memberId: this.ownerMemberId || null }
        default:
          return {}
      }
    },

    // Owner step: persist the vehicle (create or assign) with its owner (the selected existing member),
    // then advance.
    confirmOwnerStep() {
      this.saving = true
      const owner = this.resolvedOwner()
      // Already persisted (navigated back then forward again) → don't create/assign twice.
      const persist = (this.vehicleId
          ? Promise.resolve()
          : this.vehicleStep === 'create'
              ? this.createWithOwner(owner)
              : this.assignExistingWithOwner(this.foundVehicle.id, owner))

      persist
          // Load the connection offer only when the vehicle belongs to an org (a customer-owned vehicle
          // can never be connected). No org / no offer → finish here, without asking to connect.
          .then(() => this.willHaveOrganization
              ? VehicleRepository.getConnectableFormulas(this.vehicleId).catch(() => [])
              : [])
          .then(formulas => {
            this.formulas = formulas || []
            this.saving = false
            if (this.connectionAvailable) {
              this.next() // → the "connect?" step
            } else {
              this.finishVehicle()
            }
          })
          .catch(err => {
            this.saving = false
            if (err) this.notifyError(err)
          })
    },

    finishVehicle() {
      this.$emit('vehicle-added')
      this.$dialog.notify.success(this.$t('vehicle.wizard.vehicle-created'))
      this.close()
    },

    createWithOwner(owner, force = false) {
      const data = { ...this.createForm, registrationNumber: this.registrationNumber }
      if (owner.organizationId) data.organizationId = owner.organizationId
      if (owner.customerId) data.customerId = owner.customerId
      if (force) data.force = true

      this.showLoading(true)
      return VehicleRepository.create(data).then(res => {
        this.showLoading(false)
        this.vehicleId = res.id
        this.$emit('vehicle-added')
        // Org + optional member with no customer in the payload → attach the member now. The vehicle was
        // just created org-owned, so this is an assignment FROM the organization TO the member (recorded in
        // the assignment history) — not a transfer with a prior owner. Passing from> 'organization' is what
        // avoids the "fromType, toType and toId are required" 400 that the toType-only call triggered.
        if (owner.memberId && !owner.customerId) {
          return VehicleRepository.transfer(this.vehicleId, { fromType: 'organization', toType: 'member', toId: owner.memberId })
        }
      }).catch(err => {
        this.showLoading(false)
        const action = this.vehicleConflictAction(err)
        if (!force && action) {
          return this.$dialog.confirm({ text: this.$t(action.confirmKey) }).then(ok => {
            if (ok) return this.createWithOwner(owner, true)
            return Promise.reject()
          })
        }
        this.notifyError(err)
        return Promise.reject()
      })
    },

    assignExistingWithOwner(vehicleId, owner, force = false) {
      this.vehicleId = vehicleId
      let promise
      if (owner.memberId) {
        promise = VehicleRepository.transfer(vehicleId, { fromType: 'member', toType: 'member', toId: owner.memberId, ...(force ? { force: true } : {}) })
      } else if (owner.organizationId) {
        promise = VehicleRepository.assignOrganization(vehicleId, force ? { organizationId: owner.organizationId, force: true } : { organizationId: owner.organizationId })
      } else if (owner.customerId) {
        promise = VehicleRepository.assignCustomer(vehicleId, force ? { customerId: owner.customerId, force: true } : { customerId: owner.customerId })
      } else {
        return Promise.resolve()
      }

      this.showLoading(true)
      return promise.then(() => {
        this.showLoading(false)
        this.$emit('vehicle-added')
      }).catch(err => {
        this.showLoading(false)
        const action = this.vehicleConflictAction(err)
        if (!force && action) {
          return this.$dialog.confirm({ text: this.$t(action.confirmKey) }).then(ok => {
            if (ok) return this.assignExistingWithOwner(vehicleId, owner, true)
            return Promise.reject()
          })
        }
        this.notifyError(err)
        return Promise.reject()
      })
    },

    // --- navigation ---
    next() {
      if (this.stepIndex < this.visibleSteps.length - 1) this.stepIndex++
    },

    back() {
      if (this.currentKey === 'vehicle' && this.vehicleStep !== 'lookup') {
        this.vehicleStep = 'lookup'
        return
      }
      if (this.stepIndex > 0) this.stepIndex--
    },

    // --- connect gate --- (vehicle + owner + formulas already resolved by the owner step)
    goConnectionStep() {
      // Default the usage to the only available mode when there is just one, then the type.
      const modes = [...new Set(this.formulas.map(f => f.mode))]
      this.connectionMode = modes.length === 1 ? modes[0] : (this.modeAvailable('meter') ? 'meter' : 'telemetry')
      this.connectionType = this.typeOptions.find(o => o.available)?.value || null
      this.wantsConnection = true
      this.next()
    },

    modeAvailable(mode) {
      return this.formulas.some(f => f.mode === mode)
    },

    vinRule(v) {
      if (!v) return this.$t('required')
      return /^[A-HJ-NPR-Z0-9]{17}$/.test(v) || this.$t('vehicle.wizard.vin-invalid')
    },

    finishWithoutConnection() {
      this.finishVehicle()
    },

    // Only send the consent page. The meter, the reimbursement licence and the provider registration are all
    // materialised when the customer consents — the chosen config (mode, type, VIN) travels on the consent,
    // and the home location is collected there too, so nothing is created before it is granted.
    finishWithConnection() {
      if (!this.resolvedFormula) return
      this.saving = true

      const consent = { mode: this.connectionMode, type: this.connectionType }
      if (this.connectionType === 'vin' && this.vin) consent.vin = this.vin

      VehicleRepository.requestTelematicsConsent(this.vehicleId, consent)
          .then(() => {
            this.saving = false
            this.$emit('vehicle-added')
            this.$dialog.notify.success(this.$t('vehicle.wizard.consent-sent'))
            this.close()
          })
          .catch(err => {
            this.saving = false
            this.notifyError(err)
          })
    },

    formatPrice(price) {
      return price + ' €'
    },
  },
}
</script>

<style lang="scss">
.vehicle-wizard {
  .vw-steps { display: flex; gap: 6px; flex-wrap: wrap; }
  .vw-step {
    display: flex; align-items: center; gap: 6px; padding: 4px 10px; border-radius: 999px;
    background: #f4f6f8; color: #607d8b; font-size: 12px; font-weight: 600;
  }
  .vw-step__num { width: 18px; height: 18px; border-radius: 50%; display: flex; align-items: center; justify-content: center; background: #cfd8dc; color: #fff; font-size: 11px; }
  .vw-step--active .vw-step__num { background: var(--v-primary-base, #e53c22); }
  .vw-step--done .vw-step__num { background: #4caf50; }
  .vw-body { animation: vwfade .2s ease; }
  @keyframes vwfade { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: none; } }
}
</style>
